import { useState } from "react";
import { motion } from "framer-motion";
import { AppleLogo, GooglePlayLogo, EnvelopeSimple, ArrowRight } from "@phosphor-icons/react";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import { joinWaitlist } from "@/lib/api";
import { toast } from "sonner";

export default function AppDownload() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await joinWaitlist({ email, role: "customer" });
      toast.success(res.message);
      setEmail("");
    } catch {
      toast.error("Couldn't sign you up. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="download" className="relative py-24 lg:py-32 border-t border-ih-line overflow-hidden" data-testid="home-download">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[600px] ih-radial-blue" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] ih-radial-neon" />

      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative bg-ih-surface border border-ih-line2 rounded-[2rem] p-10 lg:p-16 ih-noise overflow-hidden"
        >
          <div className="absolute inset-0 ih-grid-bg opacity-30 [mask-image:radial-gradient(50%_50%_at_50%_50%,#000,transparent)]" />

          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-7">
              <SectionEyebrow label="Launching soon · Trinidad & Tobago" color="neon" />
              <h2 className="font-display font-extrabold text-4xl lg:text-6xl text-white leading-[1.02] text-balance">
                The Caribbean&apos;s delivery <br />
                platform is <span className="text-ih-neon">coming.</span>
              </h2>
              <p className="text-white/65 text-lg max-w-xl leading-relaxed">
                Be first in line. Join the waitlist and we&apos;ll text you the moment IslandHop goes live
                in your area.
              </p>

              <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 max-w-xl" data-testid="download-waitlist-form">
                <div className="flex-1 flex items-center gap-2 px-4 rounded-full border border-ih-line2 bg-black/40 focus-within:border-ih-neon transition">
                  <EnvelopeSimple size={18} className="text-white/40" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    data-testid="download-waitlist-email"
                    className="flex-1 bg-transparent py-3 outline-none text-base text-white placeholder:text-white/30"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="download-waitlist-submit"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-ih-neon text-black font-semibold hover:scale-[1.02] disabled:opacity-60 transition"
                >
                  {loading ? "Adding..." : "Join Waitlist"} <ArrowRight weight="bold" size={16} />
                </button>
              </form>

              <div className="flex flex-wrap gap-3 pt-3">
                <StoreButton platform="App Store" sub="Coming Soon" icon={AppleLogo} />
                <StoreButton platform="Google Play" sub="Coming Soon" icon={GooglePlayLogo} />
              </div>
            </div>

            <div className="lg:col-span-5">
              <PhoneMock />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const StoreButton = ({ platform, sub, icon: Icon }) => (
  <button
    type="button"
    data-testid={`store-btn-${platform.toLowerCase().replace(/\s+/g, "-")}`}
    className="group inline-flex items-center gap-3 pl-4 pr-5 py-3 rounded-2xl bg-black border border-ih-line2 hover:border-white/30 transition cursor-not-allowed"
  >
    <Icon weight="fill" size={26} className="text-white" />
    <div className="text-left">
      <p className="text-[10px] uppercase tracking-[0.18em] text-white/40">{sub}</p>
      <p className="text-sm font-semibold text-white">{platform}</p>
    </div>
  </button>
);

const PhoneMock = () => (
  <div className="relative mx-auto w-[280px] h-[560px] rounded-[40px] bg-black border-2 border-ih-line2 p-3 shadow-[0_30px_80px_-20px_rgba(0,87,255,0.45)]">
    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-black rounded-b-2xl z-10" />
    <div className="relative w-full h-full rounded-[32px] bg-gradient-to-b from-[#0a0d18] via-[#050505] to-[#050505] overflow-hidden">
      {/* status */}
      <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-[10px] text-white/60 font-mono">
        <span>9:41</span>
        <span>●●●</span>
      </div>
      <div className="absolute top-12 left-4 right-4">
        <p className="text-[11px] text-ih-neon font-mono">● Live tracking</p>
        <p className="text-white font-display font-extrabold text-xl mt-1">Order #IH-4821</p>
        <p className="text-white/50 text-xs">Marcus is on the way · 12 min</p>
      </div>
      {/* map blob */}
      <div className="absolute inset-x-4 top-32 bottom-32 rounded-2xl border border-ih-line2 overflow-hidden">
        <div className="absolute inset-0 ih-grid-bg opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-br from-ih-electric/20 via-transparent to-ih-neon/20" />
        <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full">
          <path d="M20 160 Q 60 100 90 110 T 180 40" stroke="#D4AF37" strokeWidth="2" fill="none" strokeDasharray="4 4" />
          <circle cx="20" cy="160" r="6" fill="#14B8A6" />
          <circle cx="180" cy="40" r="6" fill="#FF7A45" />
        </svg>
      </div>
      <div className="absolute bottom-6 left-4 right-4 ih-glass rounded-2xl p-3 flex items-center justify-between">
        <div>
          <p className="text-[10px] text-white/50">Total</p>
          <p className="font-display font-extrabold text-white">$148.50 TT</p>
        </div>
        <span className="px-3 py-1.5 rounded-full bg-ih-neon text-black text-[10px] font-bold">PAID</span>
      </div>
    </div>
  </div>
);
